import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, Pagination, Switch, notification } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import WelcomeDrawerReviewItem from "pages/Welcome/WelcomeDrawerReviewItem";
import { useWelcomeDrawerReviewHighlights } from "pages/Welcome/useWelcomeDrawerReviewHighlights";
import { useWelcomeDrawerReviewFeedbackState } from "pages/Welcome/useWelcomeDrawerReviewFeedbackState";
import { sortReviewsNewestFirst } from "pages/Welcome/welcomeDrawerReviewDoc";
import { downloadWelcomeDrawerReviews } from "pages/Welcome/welcomeDrawerReviewsExport";
import FileTypePopup from "helperComponents/FileTypePopup";
import { apiRequest } from "util/services";
import listStyles from "pages/Welcome/WelcomeDrawerReviewsList.module.scss";
import industryListStyles from "pages/IndustryDashboard/helperComponents/ReviewsList/style.module.scss";

const DEFAULT_PAGE_SIZE = 5;

/**
 * Shared reviews list for Welcome / KPI / dashboard side drawers.
 * Uses dashboard ReviewItem for feedback parity with console ReviewsList.
 */
const WelcomeDrawerReviewsList = ({
  allReviews,
  highlights: externalHighlights,
  autoFetchHighlights,
  pageSize = DEFAULT_PAGE_SIZE,
  serverSide = false,
  total: serverTotal,
  currentPage: serverCurrentPage = 1,
  onServerPageChange,
  reviewsLoading = false,
  showTotalCount = false,
  loadAllReviewsForExport,
  exportFileLabel,
  theme: themeProp,
}) => {
  const { t, i18n } = useTranslation();
  const reduxTheme = useSelector((state) => state.DashboardReducer?.theme);
  const theme = themeProp ?? reduxTheme ?? "light";
  const isFeedbackEnabled = useSelector(
    (state) => state.userReducer?.isFeedbackEnabled,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [feedbackModeEnabled, setFeedbackModeEnabled] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [showFileTypePopup, setShowFileTypePopup] = useState(false);
  const [fileType, setFileType] = useState("csv");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const shouldAutoFetchHighlights =
    autoFetchHighlights ?? externalHighlights === undefined;

  const feedbackState = useWelcomeDrawerReviewFeedbackState(feedbackModeEnabled);

  useEffect(() => {
    if (!isFeedbackEnabled) {
      setFeedbackModeEnabled(false);
      return undefined;
    }
    let cancelled = false;
    apiRequest("users/feedback_mode", "POST", {}, true).then((response) => {
      if (cancelled) return;
      setFeedbackModeEnabled(response?.success === true);
    });
    return () => {
      cancelled = true;
    };
  }, [isFeedbackEnabled]);

  const toggleFeedbackMode = useCallback(async () => {
    const next = !feedbackModeEnabled;
    const result = await apiRequest(
      "users/feedback_mode/setup",
      "POST",
      { isFeedbackModeOn: next },
      true,
    );
    if (result?.success) {
      setFeedbackModeEnabled(next);
    } else {
      notification.error({
        message: result?.error || t("notifications.89", "Couldn't get the mode"),
      });
    }
    setSwitchLoading(false);
  }, [feedbackModeEnabled, t]);

  const orderedReviews = useMemo(
    () => sortReviewsNewestFirst(allReviews),
    [allReviews],
  );

  useEffect(() => {
    if (!serverSide) {
      setCurrentPage(1);
    }
  }, [serverSide, orderedReviews?.length, orderedReviews?.[0]?.date_gpid]);

  const pagedReviews = useMemo(() => {
    if (serverSide) {
      return orderedReviews?.length ? orderedReviews : [];
    }
    if (!orderedReviews?.length) return [];
    const start = (currentPage - 1) * pageSize;
    return orderedReviews.slice(start, start + pageSize);
  }, [orderedReviews, currentPage, pageSize, serverSide]);

  const reviewsForHighlights = serverSide ? orderedReviews : pagedReviews;
  const {
    highlights: fetchedHighlights,
    getHighlights,
  } = useWelcomeDrawerReviewHighlights(reviewsForHighlights, {
    enabled:
      shouldAutoFetchHighlights && (reviewsForHighlights?.length ?? 0) > 0,
    notifyOnError: false,
  });
  const highlights = shouldAutoFetchHighlights
    ? fetchedHighlights
    : externalHighlights ?? {};

  const noopReviewsLoading = useCallback(() => {}, []);

  const effectivePage = serverSide ? serverCurrentPage : currentPage;
  const setEffectivePage = serverSide
    ? (p) => onServerPageChange?.(p)
    : setCurrentPage;

  const totalReviews = serverSide
    ? (serverTotal ?? 0)
    : (allReviews?.length ?? 0);

  const handleDownloadReviews = useCallback(() => {
    if (!orderedReviews.length && !totalReviews) return;
    setShowFileTypePopup(true);
  }, [orderedReviews.length, totalReviews]);

  const resolveReviewsForExport = useCallback(async () => {
    const needsAll =
      typeof loadAllReviewsForExport === "function" &&
      (serverSide ? totalReviews > orderedReviews.length : false);
    if (!needsAll) {
      return orderedReviews;
    }
    const loaded = await loadAllReviewsForExport();
    if (!Array.isArray(loaded) || loaded.length === 0) {
      return orderedReviews;
    }
    return sortReviewsNewestFirst(loaded);
  }, [
    loadAllReviewsForExport,
    serverSide,
    totalReviews,
    orderedReviews,
  ]);

  const handleExportReviews = useCallback(async () => {
    setDownloadLoading(true);
    try {
      const reviewsToExport = await resolveReviewsForExport();
      if (!reviewsToExport.length) return;
      downloadWelcomeDrawerReviews(reviewsToExport, {
        language: i18n.language,
        label: exportFileLabel,
        fileType,
        onlySequences: false,
      });
    } finally {
      setDownloadLoading(false);
      setShowFileTypePopup(false);
    }
  }, [
    resolveReviewsForExport,
    fileType,
    i18n.language,
    exportFileLabel,
  ]);

  return (
    <div
      className={`${industryListStyles.ReviewsListContainer} ${listStyles.container} ${
        theme === "dark" ? listStyles.containerDark : ""
      }`}
    >
      {isFeedbackEnabled ? (
        <div
          className={`${listStyles.modeBar} ${
            theme === "dark" ? listStyles.modeBarDark : ""
          }`}
        >
          <span className={listStyles.modeLabel}>
            {feedbackModeEnabled
              ? t("selected_dashboard_page.154")
              : t("selected_dashboard_page.153")}
          </span>
          <Switch
            size="small"
            loading={switchLoading}
            checked={feedbackModeEnabled}
            onClick={() => {
              setSwitchLoading(true);
              toggleFeedbackMode();
            }}
          />
        </div>
      ) : null}
      {!reviewsLoading &&
      (orderedReviews.length > 0 || (showTotalCount && totalReviews > 0)) ? (
        <div className={listStyles.totalRow}>
          {showTotalCount && totalReviews > 0 ? (
            <div className={listStyles.totalLine}>
              {t("welcome.highlightsDashDrawerReviewCount", {
                count: totalReviews,
              })}
            </div>
          ) : (
            <span />
          )}
          {orderedReviews.length > 0 ? (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              className={listStyles.downloadBtn}
              onClick={handleDownloadReviews}
              aria-label={t("common.exportReviews")}
            >
              {t("common.exportReviews")}
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className={listStyles.list}>
        {!allReviews?.length && !reviewsLoading ? (
          <div
            className={`${industryListStyles.emptyState} ${listStyles.emptyState}`}
          >
            <img src="/assets/review_empty_state.svg" alt="" />
            <div className={industryListStyles.emptyStateContent}>
              {t("notifications.86")}
            </div>
          </div>
        ) : (
          pagedReviews.map((review) => (
            <WelcomeDrawerReviewItem
              key={
                review?.date_gpid ||
                review?.internalId ||
                review?.gpid ||
                review?.timestamp
              }
              review={review}
              highlightEntry={
                review?.date_gpid ? highlights[review.date_gpid] ?? null : null
              }
              feedbackModeEnabled={feedbackModeEnabled}
              feedbackState={feedbackState}
              allReviews={orderedReviews}
              getHighlights={getHighlights}
              setReviewsLoading={noopReviewsLoading}
            />
          ))
        )}
      </div>
      {totalReviews > pageSize ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "12px 0 4px",
          }}
        >
          <Pagination
            size="small"
            current={effectivePage}
            pageSize={pageSize}
            total={totalReviews}
            onChange={(p) => setEffectivePage(p)}
            showSizeChanger={false}
            disabled={
              reviewsLoading || (serverSide && !onServerPageChange)
            }
          />
        </div>
      ) : null}
      {showFileTypePopup && typeof document !== "undefined"
        ? createPortal(
            <FileTypePopup
              loading={downloadLoading}
              setShowFileTypePopup={setShowFileTypePopup}
              downloadData={handleExportReviews}
              fileType={fileType}
              setFileType={setFileType}
              showHighlightsOnly={false}
              overlayZIndex={2300}
            />,
            document.body,
          )
        : null}
    </div>
  );
};

export default WelcomeDrawerReviewsList;
